import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import SaleFormDialog from "@/components/SaleFormDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { http, formatApiError, formatEuros, formatDateFr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Euro, TrendingUp, FileSignature, CheckCircle2, Home } from "lucide-react";

export default function CollaboratorSales() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/sales/me");
      setSales(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totals = useMemo(() => sales.reduce(
    (a, s) => ({
      ca: a.ca + (s.sale_price || 0),
      agence: a.agence + (s.agency_commission || 0),
      moi: a.moi + (s.negotiator_commission || 0),
      compromis: a.compromis + (s.status === "compromis" ? 1 : 0),
      actes: a.actes + (s.status === "acte" ? 1 : 0),
    }),
    { ca: 0, agence: 0, moi: 0, compromis: 0, actes: 0 }
  ), [sales]);

  const submit = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await http.patch(`/sales/${editing.id}`, payload);
        toast.success("Vente mise à jour");
      } else {
        await http.post("/sales", payload);
        toast.success("Vente enregistrée");
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await http.delete(`/sales/${deleting.id}`);
      toast.success("Vente supprimée");
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Layout>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-testid="collaborator-sales">
        <div>
          <p className="label-eyebrow mb-2">Chiffre d'affaires</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Mes ventes</h1>
          <p className="mt-2 text-slate-600">Enregistrez vos compromis et actes signés — commissions incluses.</p>
        </div>
        <Button size="lg" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-sale-button" className="gap-2">
          <Plus className="h-4 w-4" /> Ajouter une vente
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <BigMoney icon={TrendingUp} label="CA total" value={formatEuros(totals.ca)} tone="blue" />
        <BigMoney icon={Euro} label="Commission agence" value={formatEuros(totals.agence)} tone="amber" />
        <BigMoney icon={Euro} label="Ma commission" value={formatEuros(totals.moi)} tone="emerald" bold />
        <BigMoney icon={FileSignature} label="Compromis" value={totals.compromis} tone="slate" />
        <BigMoney icon={CheckCircle2} label="Actes" value={totals.actes} tone="slate" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : sales.length === 0 ? (
        <Card className="p-12 text-center text-slate-500" data-testid="sales-empty">
          Aucune vente enregistrée. Cliquez sur "Ajouter une vente" pour démarrer.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sales.map((s) => (
            <Card key={s.id} className="p-5 hover:shadow-md transition-all duration-200" data-testid={`sale-card-${s.id}`}>
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant={s.status === "acte" ? "default" : "secondary"} className="uppercase tracking-wider text-[10px]">
                      {s.status === "acte" ? "Acte définitif" : "Compromis"}
                    </Badge>
                    {s.is_co_sale ? (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider gap-1 border-primary/40 text-primary bg-primary/5">
                        Co-vente
                      </Badge>
                    ) : null}
                    <span className="text-xs text-slate-500">{formatDateFr(s.signed_date)}</span>
                  </div>
                  <div className="font-display font-semibold text-lg text-slate-900 flex items-start gap-1.5">
                    <Home className="h-4 w-4 text-slate-400 mt-1 shrink-0" />
                    <span className="truncate">{s.property_address || "—"}</span>
                  </div>
                  {s.client_name && <div className="text-xs text-slate-500 mt-1">Client : {s.client_name}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => { setEditing(s); setDialogOpen(true); }} data-testid={`edit-sale-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => setDeleting(s)} data-testid={`delete-sale-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border text-sm">
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Prix</div><div className="font-semibold text-slate-900">{formatEuros(s.sale_price)}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Com. agence</div><div className="font-semibold text-slate-900">{formatEuros(s.agency_commission)}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ma part</div><div className="font-display font-bold text-emerald-600">{formatEuros(s.negotiator_commission)}</div></div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <SaleFormDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSubmit={submit}
        initial={editing}
        saving={saving}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette vente ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est définitive.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="confirm-delete-sale">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function BigMoney({ icon: Icon, label, value, tone = "blue", bold = false }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <Card className={`p-5 ${bold ? "border-emerald-300 ring-2 ring-emerald-100" : ""}`} data-testid={`money-${label}`}>
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ring-1 ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
          <div className={`font-display font-bold text-xl truncate ${bold ? "text-emerald-600" : "text-slate-900"}`}>{value}</div>
        </div>
      </div>
    </Card>
  );
}
